import React, { useMemo } from 'react'

import { Table, TableColumnsType } from 'antd'

import { DownloadLink } from '../shared/DownloadLink'
import { formatBucketTitle } from '../shared/utils'

interface SlowQueryComparisonProps {
  data?: {
    clusterDiff?: Array<{
      key: string
      clusterA: number
      clusterB: number
      diff: number
    }>
    summary?: {
      slowQueryDigest?: {
        added?: number
        removed?: number
        Stable?: number
        ImprovedPerformance?: number
        DegradedPerformance?: number
        diffDetail?: {
          buckets?: Array<Record<string, number>>
        }
      }
    }
    slowQueryDigestDiff?: {
      digestDiff?: Array<{
        digest: string
        label: string
        diffInfos: Array<{
          key: string
          clusterA: number
          clusterB: number
          diff: number
        }>
      }>
    }
  }
}

interface SlowQueryData {
  slowquery_count: { formatted: string; diffPercent: number } | null
  slowquery_digest: { formatted: string; diffPercent: number } | null
  added: number | null
  removed: number | null
  Stable: number | null
  ImprovedPerformance: number | null
  DegradedPerformance: number | null
  '[\u003c-50]': number | null
  '[-50~-20]': number | null
  '[-20~-5]': number | null
  '[-5~5]': number | null
  '[5~20]': number | null
  '[20~50]': number | null
  '[\u003e50]': number | null
}

// 格式化指标值显示：130474 -> 48402 (↓-62.9%)
function formatMetricValue(
  clusterA: number,
  clusterB: number,
  diffPercent: number
): { formatted: string; diffPercent: number } {
  const arrow = diffPercent > 0 ? '↑' : diffPercent < 0 ? '↓' : '→'
  const sign = diffPercent > 0 ? '+' : ''
  const percentStr = `${sign}${diffPercent}%`
  return {
    formatted: `${clusterA.toLocaleString()} → ${clusterB.toLocaleString()} (${arrow}${percentStr})`,
    diffPercent
  }
}

export const SlowQueryComparison: React.FC<SlowQueryComparisonProps> = ({
  data
}) => {
  const slowQueryData = useMemo<SlowQueryData>(() => {
    const result: SlowQueryData = {
      slowquery_count: null,
      slowquery_digest: null,
      added: null,
      removed: null,
      Stable: null,
      ImprovedPerformance: null,
      DegradedPerformance: null,
      '[\u003c-50]': null,
      '[-50~-20]': null,
      '[-20~-5]': null,
      '[-5~5]': null,
      '[5~20]': null,
      '[20~50]': null,
      '[\u003e50]': null
    }

    // 从 clusterDiff 中提取数据
    if (data?.clusterDiff) {
      const slowQueryCount = data.clusterDiff.find(
        (item) => item.key === 'slowquery_count'
      )
      if (slowQueryCount) {
        result.slowquery_count = formatMetricValue(
          slowQueryCount.clusterA,
          slowQueryCount.clusterB,
          slowQueryCount.diff
        )
      }

      const slowQueryDigest = data.clusterDiff.find(
        (item) => item.key === 'slowquery_digest'
      )
      if (slowQueryDigest) {
        result.slowquery_digest = formatMetricValue(
          slowQueryDigest.clusterA,
          slowQueryDigest.clusterB,
          slowQueryDigest.diff
        )
      }
    }

    // 从 summary.slowQueryDigest 中提取数据
    if (data?.summary?.slowQueryDigest) {
      const digest = data.summary.slowQueryDigest
      result.added = digest.added ?? null
      result.removed = digest.removed ?? null
      result.Stable = digest.Stable ?? null
      result.ImprovedPerformance = digest.ImprovedPerformance ?? null
      result.DegradedPerformance = digest.DegradedPerformance ?? null

      // 从 diffDetail.buckets 中提取数据
      if (digest.diffDetail?.buckets) {
        digest.diffDetail.buckets.forEach((bucket) => {
          // bucket 是一个对象，如 { "[<-50]": 0 }
          const key = Object.keys(bucket)[0]
          const value = bucket[key]
          // 只处理区间字段
          if (key === '[\u003c-50]') {
            result['[\u003c-50]'] = value
          } else if (key === '[-50~-20]') {
            result['[-50~-20]'] = value
          } else if (key === '[-20~-5]') {
            result['[-20~-5]'] = value
          } else if (key === '[-5~5]') {
            result['[-5~5]'] = value
          } else if (key === '[5~20]') {
            result['[5~20]'] = value
          } else if (key === '[20~50]') {
            result['[20~50]'] = value
          } else if (key === '[>50]') {
            result['[\u003e50]'] = value
          }
        })
      }
    }

    return result
  }, [data])

  const renderMetricValue = (
    value: { formatted: string; diffPercent: number } | null
  ) => {
    if (!value) return '-'

    const { formatted, diffPercent } = value
    const arrow = diffPercent > 0 ? '↑' : diffPercent < 0 ? '↓' : '→'
    const color =
      diffPercent > 0 ? '#ff4d4f' : diffPercent < 0 ? '#52c41a' : undefined

    // 提取箭头和百分比部分，添加颜色
    const parts = formatted.split(`(${arrow}`)
    if (parts.length === 2) {
      const percentPart = parts[1].replace(')', '')
      return (
        <span>
          {parts[0]}
          <span style={{ color }}>
            ({arrow}
            {percentPart})
          </span>
        </span>
      )
    }

    return <span>{formatted}</span>
  }

  // 计算总数
  const total = useMemo(() => {
    return data?.slowQueryDigestDiff?.digestDiff?.length ?? 0
  }, [data])

  const columns: TableColumnsType<SlowQueryData> = [
    {
      title: 'Slow Query Total Count',
      dataIndex: 'slowquery_count',
      key: 'slowquery_count',
      render: (value) => renderMetricValue(value)
    },
    {
      title: 'Slow Query Digest Count',
      dataIndex: 'slowquery_digest',
      key: 'slowquery_digest',
      render: (value) => renderMetricValue(value)
    },
    {
      title: 'Added',
      dataIndex: 'added',
      key: 'added',
      render: (value) =>
        value !== null && value !== undefined ? value.toLocaleString() : '-'
    },
    {
      title: 'Removed',
      dataIndex: 'removed',
      key: 'removed',
      render: (value) =>
        value !== null && value !== undefined ? value.toLocaleString() : '-'
    },
    {
      title: `Improved (${slowQueryData.ImprovedPerformance ?? 0})`,
      key: 'Improved',
      children: [
        {
          title: formatBucketTitle('[\u003c-50]'),
          dataIndex: '[\u003c-50]',
          key: '[\u003c-50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[-50~-20]'),
          dataIndex: '[-50~-20]',
          key: '[-50~-20]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[-20~-5]'),
          dataIndex: '[-20~-5]',
          key: '[-20~-5]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: `Stable (${slowQueryData.Stable ?? 0})`,
      key: 'Stable',
      children: [
        {
          title: formatBucketTitle('[-5~5]'),
          dataIndex: '[-5~5]',
          key: '[-5~5]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: `Degraded (${slowQueryData.DegradedPerformance ?? 0})`,
      key: 'Degraded',
      children: [
        {
          title: formatBucketTitle('[5~20]'),
          dataIndex: '[5~20]',
          key: '[5~20]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[20~50]'),
          dataIndex: '[20~50]',
          key: '[20~50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        },
        {
          title: formatBucketTitle('[\u003e50]'),
          dataIndex: '[\u003e50]',
          key: '[\u003e50]',
          render: (value) =>
            value !== null && value !== undefined ? value.toLocaleString() : '-'
        }
      ]
    },
    {
      title: 'Total',
      key: 'Total',
      render: () => (total > 0 ? total.toLocaleString() : '-')
    }
  ]

  return (
    <section className='mb-6'>
      <div className='mb-4 flex items-center'>
        <h2 className='text-2xl font-semibold m-0 mr-1'>
          Slow Query Comparison
        </h2>
        <DownloadLink data={data} filename='sql_diff.json' />
      </div>
      <Table
        columns={columns}
        dataSource={[slowQueryData]}
        rowKey={() => 'slow-query-comparison'}
        pagination={false}
        bordered
        size='small'
      />
    </section>
  )
}
